import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend,
} from 'recharts';
import { motion, AnimatePresence } from 'motion/react';
import {
  Package, Wallet, Landmark, TrendingUp, DollarSign, CalendarDays,
  Trophy, History, Target, Lock, BarChart3, Activity, Heart, Search,
} from 'lucide-react';
import {
  getSoldRows, getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice,
  getTradeLock, TRADE_LOCK_DAYS,
} from './helpers';
import {
  ZONE, CANDY, getItemTypes, findSkinImage, readableInk, fmt, signed, pct, pnlColor, drop,
  ZoneHead, ZoneStats, ZoneFooter,
} from './zoneShared';
import { rowName, dayOf, usePortfolioVsMarket } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';

// Overview Zone — the Overview 3 dashboard rebuilt on the Overview 7 board.
// Same panels, same numbers, same interactions; candy paper and hard shadows.
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const TICK = { fill: '#131313', fontSize: 14.5, fontWeight: 800, fontFamily: 'Archivo, sans-serif' };
const GRID = 'rgba(19,19,19,0.14)';
const AXIS = '#131313';

// Watch key is skin+condition (never assetId) so a heart follows the skin,
// not the individual item, and survives that item being sold.
function holdingKey(row) {
  return `${row.skin?.value}|${row.condition?.value || ''}`;
}

// ── Animated count-up ────────────────────────────────────────
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    const from = prevRef.current;
    const to   = Number.isFinite(target) ? target : 0;
    prevRef.current = to;
    if (from === to) { setVal(to); return; }
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function Money({ value, withSign = false }) {
  const v = useCountUp(value ?? 0);
  return <>{withSign ? signed(v) : `$${fmt(v)}`}</>;
}

function Count({ value }) {
  const v = useCountUp(value ?? 0, 700);
  return <>{Math.round(v)}</>;
}

// ── Cylinder bar ────────────────────────────────────────────────────────
// The capsule recipe as-is: a full ellipse cap on the near end, a straight
// body with only its top and bottom edges inked, and a far cap clipped to
// its right half. The capsules run left to right in one row, each as long as
// its share of the total, so the whole row reads as everything invested and
// each segment as one category's slice of it.
const CYL_BAR_H = 56;   // cylinder diameter
const CYL_CAP_W = 28;   // horizontal extent of the ellipse caps

function CylinderColumn({ fill, label, value, share, delay }) {
  return (
    <div className="zn-cyl-col" style={{ flexGrow: Math.max(share, 0.001) }}>
      <div className="zn-cyl-value">${value.toFixed(0)}</div>
      <motion.div
        className="zn-cyl-shape"
        data-tooltip={`${label} · $${fmt(value)} · ${share.toFixed(0)}%`}
        style={{ height: CYL_BAR_H, '--zn-cap-w': `${CYL_CAP_W}px` }}
        initial={{ width: '12%', opacity: 0 }}
        animate={{ width: '100%', opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      >
        <span className="zn-cyl-body" style={{ background: fill }} />
        <span className="zn-cyl-cap far" style={{ background: fill }} />
        <span className="zn-cyl-cap near" style={{ background: fill }} />
      </motion.div>
      <div className="zn-cyl-label" title={label}>{label}</div>
    </div>
  );
}

// ── Tooltip ──────────────────────────────────────────────────
const ProfitTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="zn-tip">
      <div className="k">{label}{p.count ? ` · ${p.count} flips` : ''}</div>
      <div className="v" style={{ color: pnlColor(p.profit) }}>{signed(p.profit)}</div>
    </div>
  );
};

// ── Panel shell ──────────────────────────────────────────────
function Panel({ title, desc, icon: Icon, tools, children, i = 0 }) {
  return (
    <motion.div className="zn-panel" {...drop(i + 4, 0)}>
      <div className="zn-panel-head">
        <div style={{ minWidth: 0 }}>
          <div className="zn-panel-h">{title}</div>
          {desc && <div className="zn-panel-desc">{desc}</div>}
        </div>
        {tools && <div className="zn-panel-tools">{tools}</div>}
        {Icon && !tools && <span className="zn-icon-chip"><Icon size={16} strokeWidth={2.6} /></span>}
      </div>
      {children}
    </motion.div>
  );
}

// ── Horizontal gauge ─────────────────────────────────────────
// `display` is the headline figure and `caption` spells out the raw numbers
// behind it, so the marker on the track never has to be read on its own.
function Gauge({ name, value, display, color, low, high, caption }) {
  return (
    <div className="zn-gauge">
      <div className="zn-gauge-top">
        <span className="zn-gauge-name">{name}</span>
        <span className="zn-gauge-value">{display}</span>
      </div>
      <div className="zn-gauge-track">
        <motion.div className="zn-gauge-dot" style={{ background: color }}
          initial={{ left: '50%' }} animate={{ left: `${value}%` }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.25 }} />
      </div>
      <div className="zn-gauge-labels"><span>{low}</span><span>{high}</span></div>
      {caption && <div className="zn-gauge-cap">{caption}</div>}
    </div>
  );
}

export default function OverviewTabZone({ rows, settings, livePrices, csfloatPrices, skinsData = [], inventoryMap, watchedKeys, onToggleWatch }) {
  const [detail, setDetail]               = useState(null);   // the holding whose modal is open
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartYear, setChartYear]         = useState(new Date().getFullYear());
  const [showBars, setShowBars]           = useState(true);
  const [showArea, setShowArea]           = useState(false);
  const [holdingsQuery, setHoldingsQuery] = useState('');
  const [favOnly, setFavOnly]             = useState(false);
  const [hoveredCat, setHoveredCat]       = useState(null);

  const isExcluded = useCallback(r => getItemTypes(r).some(t => {
    const def = settings?.itemTypes?.find(it => it.value === t.value);
    return def?.overrideProfitLoss === true;
  }), [settings?.itemTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableYears = useMemo(() => {
    const years = new Set();
    rows?.forEach(r => { const y = new Date(r.timestamp).getFullYear(); if (y) years.add(y); });
    return [...years].sort((a, b) => b - a);
  }, [rows]);

  // ── Stats ──────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!rows?.length) return {};
    const sold   = getSoldRows(rows);
    const unsold = getUnsoldRows(rows);

    const includedSold  = getIncludedSold(rows, isExcluded);
    const totalProfit   = getTotalProfit(rows, isExcluded);
    const totalSoldCost = includedSold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const unsoldCost    = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const itemCount     = rows.filter(r => r.skin?.value).length;

    let unrealizedPnl = 0, marketValue = 0, gainers = 0, losers = 0;
    unsold.forEach(r => {
      const lp  = getLivePrice(livePrices, r);
      const buy = parseFloat(r.buyPrice) || 0;
      if (lp) {
        const market = lp.starting_at?.price || 0;
        marketValue   += market;
        unrealizedPnl += market - buy;
        if (market >= buy) gainers++; else losers++;
      } else {
        marketValue += buy;
      }
    });

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const profit30d = includedSold
      .filter(r => new Date(r.timestamp) >= cutoff)
      .reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);

    const realizedRoi   = totalSoldCost > 0 ? (totalProfit / totalSoldCost * 100) : null;
    const unrealizedRoi = unsoldCost    > 0 ? (unrealizedPnl / unsoldCost * 100) : null;

    // Trade lock queue
    let lockedCost = 0, lockedCount = 0, unlock7 = 0;
    const in7 = new Date();
    in7.setDate(in7.getDate() + 7);
    unsold.forEach(r => {
      const inv = r.assetId ? inventoryMap?.get(r.assetId) : null;
      const { locked, until } = getTradeLock(r, inv);
      if (!locked) return;
      lockedCost  += parseFloat(r.buyPrice) || 0;
      lockedCount += 1;
      if (until && until <= in7) unlock7 += 1;
    });

    return { totalProfit, unsoldCost, marketValue, unrealizedPnl, itemCount, profit30d,
      soldCount: sold.length, unsoldCount: unsold.length, realizedRoi, unrealizedRoi,
      gainers, losers, lockedCost, lockedCount, unlock7 };
  }, [rows, livePrices, isExcluded, inventoryMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Monthly profit ─────────────────────────────────────────
  const monthlyData = useMemo(() => {
    const months = MONTHS.map((label, i) => ({ label, month: i + 1, profit: 0, count: 0 }));
    rows?.filter(r => parseFloat(r.sellPrice) > 0 && !isExcluded(r)).forEach(r => {
      const d = new Date(r.timestamp);
      if (d.getFullYear() === chartYear) {
        const m = months[d.getMonth()];
        m.profit += parseFloat(r.profit) || 0;
        m.count  += 1;
      }
    });
    return months.map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }));
  }, [rows, chartYear, isExcluded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flips table month defaults to the current month when nothing's selected.
  const flipsMonth = selectedMonth ?? (new Date().getMonth() + 1);

  const monthFlips = useMemo(() => {
    if (!rows) return [];
    return rows
      .filter(r => {
        const d = new Date(r.timestamp);
        return d.getFullYear() === chartYear && d.getMonth() + 1 === flipsMonth
          && parseFloat(r.sellPrice) > 0;
      })
      .sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit));
  }, [rows, flipsMonth, chartYear]);

  const monthNet = useMemo(() => monthFlips.reduce((s, r) => s + (parseFloat(r.profit) || 0), 0), [monthFlips]);

  // ── Portfolio mix ──────────────────────────────────────────
  const categoryData = useMemo(() => {
    if (!rows) return [];
    const liveRows = rows.filter(r => r.skin?.value && parseFloat(r.buyPrice) > 0);
    const typed = (settings?.itemTypes || [])
      .filter(t => t.value !== 'Normal')
      .map(type => {
        const typeRows = liveRows.filter(r => getItemTypes(r).some(t => t.value === type.value));
        const value = typeRows.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
        return { name: type.label, value: parseFloat(value.toFixed(2)), color: type.color, count: typeRows.length };
      })
      .filter(d => d.value > 0);

    const uncategorized = liveRows.filter(r => getItemTypes(r).length === 0);
    const uncatValue = uncategorized.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    if (uncatValue > 0) {
      // Pinned colour, like Overview 3: left to the candy fallback it could
      // collide with a real category that also has no colour set.
      typed.push({ name: 'Uncategorized', value: parseFloat(uncatValue.toFixed(2)), color: '#a8a49a', count: uncategorized.length });
    }
    return typed;
  }, [rows, settings]);

  // Candy palette by default so the board stays one colour system; a category
  // with its own colour set in Settings keeps that colour.
  const catColors = useMemo(
    () => categoryData.map((d, i) => d.color || CANDY[i % CANDY.length]),
    [categoryData],
  );
  const totalCat = useMemo(() => categoryData.reduce((s, d) => s + d.value, 0), [categoryData]);

  // ── Top holdings ───────────────────────────────────────────
  const allHoldings = useMemo(() => {
    if (!rows) return [];
    return getUnsoldRows(rows)
      // getUnsoldRows, not a hand-rolled sellPrice test: an item traded away
      // keeps an empty sellPrice forever and would still be listed as a holding.
      .filter(r => parseFloat(r.buyPrice) > 0)
      .sort((a, b) => parseFloat(b.buyPrice) - parseFloat(a.buyPrice));
  }, [rows]);

  const topItems = useMemo(() => {
    const q = holdingsQuery.trim().toLowerCase();
    return allHoldings
      .filter(r => {
        if (favOnly && !watchedKeys?.has(holdingKey(r))) return false;
        if (q && !r.skin?.value?.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice(0, 9);
  }, [allHoldings, holdingsQuery, favOnly, watchedKeys]);

  // ── Recent activity ────────────────────────────────────────
  const recent = useMemo(() => {
    if (!rows) return [];
    return [...rows]
      .filter(r => r.skin?.value)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 6);
  }, [rows]);

  // ── Trading performance ────────────────────────────────────
  const perf = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (!sold.length) return null;
    const profits = sold.map(r => parseFloat(r.profit) || 0);
    const wins    = profits.filter(p => p > 0).length;
    let best = sold[0], worst = sold[0];
    sold.forEach(r => {
      const p = parseFloat(r.profit) || 0;
      if (p > (parseFloat(best.profit)  || 0)) best  = r;
      if (p < (parseFloat(worst.profit) || 0)) worst = r;
    });
    return {
      count: sold.length, wins,
      winRate: wins / sold.length * 100,
      avg: profits.reduce((s, p) => s + p, 0) / sold.length,
      best, worst,
    };
  }, [rows, isExcluded]);

  // ── You against the market ────────────────────────────────
  // The one gauge here that is NOT about you alone: the archive prices each
  // holding on the day it was bought and the chain-linked index says what the
  // market did over those same months. See usePortfolioVsMarket for why the
  // benchmark is computed per item rather than from one start date.
  const marketPoints = useMemo(() => (rows || [])
    .filter(r => r.skin?.value
      && !(parseFloat(r.sellPrice) > 0)
      && !(r.tradeId || r.tradedFor?.length > 0)
      && !isExcluded(r))
    .map((r, i) => ({ key: `hold${i}`, name: rowName(r), date: dayOf(r.timestamp) })),
    [rows, isExcluded]);
  const vsMarket = usePortfolioVsMarket(marketPoints, csfloatPrices);

  // ── Health checks ──────────────────────────────────────────
  // Questions about your own trading, not about the CS2 market — except the
  // last one, which is exactly that comparison. Each gauge ships with a
  // caption naming the raw numbers it came from.
  const gauges = useMemo(() => {
    const col = (v) => v < 40 ? ZONE.hot : v < 60 ? ZONE.orange : ZONE.green;

    // 1. Last 30 days measured against a typical month of realized profit.
    const monthTotals = {};
    (rows || []).filter(r => parseFloat(r.sellPrice) > 0 && !isExcluded(r)).forEach(r => {
      const d = new Date(r.timestamp);
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      monthTotals[k] = (monthTotals[k] || 0) + (parseFloat(r.profit) || 0);
    });
    const abs    = Object.values(monthTotals).map(Math.abs).filter(v => v > 0);
    const avgAbs = abs.length ? abs.reduce((s, v) => s + v, 0) / abs.length : 0;
    const p30    = stats.profit30d ?? 0;
    const momentum = Math.round(50 + 50 * Math.max(-1, Math.min(1, p30 / (avgAbs || 1))));

    // 2. Share of the items you hold that are currently above their buy price.
    const gainers = stats.gainers ?? 0;
    const losers  = stats.losers ?? 0;
    const known   = gainers + losers;
    const gainPct = known ? Math.round(gainers / known * 100) : 50;

    // 3. Share of completed sales that made money.
    const winRate = perf ? Math.round(perf.winRate) : 50;

    return [
      {
        name: 'Recent form', value: momentum,
        display: avgAbs > 0 ? `${momentum}/100` : 'n/a', color: col(momentum),
        low: 'Slower than usual', high: 'Better than usual',
        caption: avgAbs > 0
          ? `You banked ${signed(p30)} in the last 30 days. A typical month for you is $${fmt(avgAbs)}.`
          : 'Not enough months of sales yet to compare against.',
      },
      {
        name: 'Holdings in profit', value: gainPct,
        display: known ? `${gainPct}%` : 'n/a', color: col(gainPct),
        low: 'Most are down', high: 'Most are up',
        caption: known
          ? `${gainers} of the ${known} items you hold are worth more than you paid.`
          : 'Waiting on live prices for the items you hold.',
      },
      {
        name: 'Win rate', value: winRate,
        display: perf ? `${winRate}%` : 'n/a', color: col(winRate),
        low: 'Mostly losses', high: 'Mostly wins',
        caption: perf
          ? `${perf.wins} of your ${perf.count} completed sales made money.`
          : 'No completed sales yet.',
      },
      // Only when the archive priced something, so a server without one leaves
      // the board exactly as it was.
      ...(vsMarket ? [{
        name: 'You vs the market',
        // 50 = you matched it. Twenty percentage points either way saturates.
        value: Math.round(50 + 50 * Math.max(-1, Math.min(1, (vsMarket.yourPct - vsMarket.marketPct) / 20))),
        display: `${vsMarket.yourPct - vsMarket.marketPct >= 0 ? '+' : '−'}${Math.abs(vsMarket.yourPct - vsMarket.marketPct).toFixed(1)}pp`,
        color: col(50 + 50 * Math.max(-1, Math.min(1, (vsMarket.yourPct - vsMarket.marketPct) / 20))),
        low: 'Market did better', high: 'You did better',
        caption: `Your ${vsMarket.n} priced holding${vsMarket.n === 1 ? ' is' : 's are'} ${
          vsMarket.yourPct >= 0 ? 'up' : 'down'} ${Math.abs(vsMarket.yourPct).toFixed(1)}% since you bought. `
          + `The market moved ${vsMarket.marketPct >= 0 ? 'up' : 'down'} ${Math.abs(vsMarket.marketPct).toFixed(1)}% over the same stretch.`,
      }] : []),
    ];
  }, [rows, isExcluded, stats.profit30d, stats.gainers, stats.losers, perf, vsMarket]);

  const portfolioHistory = useMemo(() => settings?.portfolioHistory || [], [settings]);

  const noData = !rows?.filter(r => r.skin?.value).length;
  if (noData) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="BIG" post="BOARD" sub="Your whole operation on one wall." />
        <div className="zn-empty">No data yet. Add some items in the Tracker and the board fills up.</div>
      </div>
    );
  }

  const bestP  = perf ? parseFloat(perf.best.profit)  || 0 : 0;
  const worstP = perf ? parseFloat(perf.worst.profit) || 0 : 0;

  return (
    <div className="zn-root">

      {/* ── Header ─────────────────────────────────── */}
      <ZoneHead
        pre="THE" hl="BIG" post="BOARD"
        sub="Everything at once: what you own, what it's worth, and how the flipping is going."
        stickers={[
          { text: `${stats.itemCount ?? 0} items tracked`, bg: ZONE.teal },
          { text: livePrices ? 'prices: LIVE ✷' : 'prices: loading…', bg: ZONE.lime },
        ]}
      >
        {perf && (
          <motion.div className="zn-burst-wrap zn-spacer" {...drop(3)}>
            <div className="zn-burst" />
            <div className="zn-burst-label">
              <b>{Math.round(perf.winRate)}%</b>
              <span>win rate</span>
            </div>
          </motion.div>
        )}
      </ZoneHead>

      {/* ── Stat cards ─────────────────────────────── */}
      <ZoneStats items={[
        { label: 'Total items', icon: Package, value: <Count value={stats.itemCount ?? 0} />,
          sub: `${stats.soldCount ?? 0} sold · ${stats.unsoldCount ?? 0} holding`, bg: ZONE.white },
        { label: 'Total invested', icon: Wallet, value: <Money value={stats.unsoldCost ?? 0} />,
          sub: `what the ${stats.unsoldCount ?? 0} items you hold cost you`, bg: ZONE.yellow },
        { label: 'Market value', icon: Landmark, value: <Money value={stats.marketValue ?? 0} />,
          sub: "what they're worth at buff163 prices", bg: ZONE.purple },
        { label: 'If you sold today', icon: TrendingUp, value: <Money value={stats.unrealizedPnl ?? 0} withSign />,
          color: pnlColor(stats.unrealizedPnl ?? 0),
          sub: stats.unrealizedRoi != null ? `${pct(stats.unrealizedRoi)} vs. what you paid` : 'no live prices yet',
          bg: (stats.unrealizedPnl ?? 0) >= 0 ? ZONE.teal : ZONE.pink },
        { label: 'Profit banked', icon: DollarSign, value: <Money value={stats.totalProfit ?? 0} withSign />,
          color: pnlColor(stats.totalProfit ?? 0),
          sub: stats.realizedRoi != null ? `${pct(stats.realizedRoi)} return on items sold` : 'nothing sold yet',
          bg: (stats.totalProfit ?? 0) >= 0 ? ZONE.lime : ZONE.hot },
        { label: '30-day profit', icon: CalendarDays, value: <Money value={stats.profit30d ?? 0} withSign />,
          color: pnlColor(stats.profit30d ?? 0), sub: 'realized, last 30 days', bg: ZONE.white },
      ]} />

      {/* ── Monthly profit + portfolio mix ─────────── */}
      <div className="zn-cols c-main">

        <Panel i={0} title="Monthly profit" desc="Realized profit by month · click a bar or pill for details"
          tools={
            <>
              <button type="button" className={`zn-chip icon ${showBars ? 'on' : ''}`}
                onClick={() => setShowBars(v => (v && !showArea) ? v : !v)}
                title={showBars ? 'Hide bars' : 'Show bars'}>
                <BarChart3 size={15} strokeWidth={2.6} />
              </button>
              <button type="button" className={`zn-chip icon ${showArea ? 'on' : ''}`}
                onClick={() => setShowArea(v => (v && !showBars) ? v : !v)}
                title={showArea ? 'Hide area' : 'Show area'}>
                <Activity size={15} strokeWidth={2.6} />
              </button>
              {availableYears.map(y => (
                <button key={y} type="button" className={`zn-chip sm ${chartYear === y ? 'on' : ''}`}
                  onClick={() => { setChartYear(y); setSelectedMonth(null); }}>
                  {y}
                </button>
              ))}
            </>
          }>
          <div className="zn-panel-body" style={{ padding: '14px 8px 0' }}>
            <ResponsiveContainer width="100%" height={236}>
              <ComposedChart data={monthlyData} margin={{ top: 10, right: 18, left: 0, bottom: 0 }} barCategoryGap="26%">
                <defs>
                  <linearGradient id="znOvArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={ZONE.green} stopOpacity={0.75} />
                    <stop offset="95%" stopColor={ZONE.green} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} dy={4} />
                <YAxis tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false} tickFormatter={v => `$${v}`} width={54} />
                <Tooltip content={<ProfitTip />} cursor={{ fill: 'rgba(19,19,19,0.06)' }} />
                <ReferenceLine y={0} stroke={AXIS} strokeWidth={3} />
                {showArea && (
                  <Area type="monotone" dataKey="profit" stroke={AXIS} strokeWidth={3}
                    fillOpacity={1} fill="url(#znOvArea)" dot={false}
                    activeDot={{
                      fill: ZONE.yellow, stroke: AXIS, strokeWidth: 3, r: 6, cursor: 'pointer',
                      onClick: (_, e) => {
                        const m = e?.payload?.month;
                        if (m) setSelectedMonth(prev => prev === m ? null : m);
                      },
                    }}
                    animationBegin={200} animationDuration={1300} />
                )}
                {showBars && (
                  <Bar dataKey="profit" maxBarSize={42} animationDuration={850} animationEasing="ease-out"
                    onClick={(entry) => {
                      const m = entry?.month ?? entry?.payload?.month;
                      if (m) setSelectedMonth(prev => prev === m ? null : m);
                    }}
                    cursor="pointer" stroke={AXIS} strokeWidth={3}>
                    {monthlyData.map((m, i) => (
                      <Cell key={i}
                        fill={selectedMonth === m.month
                          ? (m.profit >= 0 ? ZONE.yellow : ZONE.orange)
                          : (m.profit >= 0 ? ZONE.green : ZONE.hot)} />
                    ))}
                  </Bar>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Month pills */}
          <div className="zn-month-strip">
            {monthlyData.map(m => (
              <button key={m.month} type="button"
                className={`zn-month-pill ${selectedMonth === m.month ? 'on' : ''}`}
                onClick={() => setSelectedMonth(selectedMonth === m.month ? null : m.month)}>
                <span className="m">{m.label}</span>
                <span className="v" style={{ color: m.profit !== 0 ? pnlColor(m.profit) : ZONE.mute }}>
                  {m.profit !== 0 ? `${m.profit >= 0 ? '+' : '-'}$${Math.abs(m.profit).toFixed(0)}` : '·'}
                </span>
              </button>
            ))}
          </div>

          {/* Selected month detail */}
          <AnimatePresence>
            {selectedMonth && monthFlips.length > 0 && (
              <motion.div className="zn-detail"
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
                <div className="zn-detail-title">
                  {MONTHS[selectedMonth - 1]} {chartYear} — {monthFlips.length} flips
                </div>
                {monthFlips.map((r, i) => {
                  const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                  const p = parseFloat(r.profit) || 0;
                  return (
                    <motion.div key={i} className="zn-row"
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03, duration: 0.25 }}>
                      {img && <img src={img} alt="" />}
                      <div className="zn-row-main">
                        <div className="zn-row-name">{r.skin?.value}</div>
                        <div className="zn-row-meta">{r.condition?.value || 'n/a'}</div>
                      </div>
                      <div className="zn-row-num" style={{ color: pnlColor(p) }}>{signed(p)}</div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </Panel>

        <Panel i={1} title="Where your money is" desc="Split by category, based on what you paid">
          <div className="zn-panel-body">
            {categoryData.length === 0 ? (
              <div className="zn-empty" style={{ padding: '30px 14px' }}>No categorized items</div>
            ) : (
              <>
                <div className="zn-donut-wrap">
                  <ResponsiveContainer width="100%" height={212}>
                    <PieChart>
                      <Pie data={categoryData} cx="50%" cy="50%"
                        innerRadius="68%" outerRadius="100%" paddingAngle={3} minAngle={8}
                        dataKey="value" stroke={AXIS} strokeWidth={3}
                        animationDuration={900} animationEasing="ease-out"
                        onMouseEnter={(_, i) => setHoveredCat(i)}
                        onMouseLeave={() => setHoveredCat(null)}>
                        {categoryData.map((entry, i) => (
                          <Cell key={i} fill={catColors[i]}
                            opacity={hoveredCat === null || hoveredCat === i ? 1 : 0.4}
                            style={{ cursor: 'pointer', transition: 'opacity 0.15s ease' }} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="zn-donut-center">
                    <AnimatePresence mode="wait" initial={false}>
                      {hoveredCat !== null && categoryData[hoveredCat] ? (
                        <motion.div key={`cat-${hoveredCat}`}
                          initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.5 }} transition={{ duration: 0.18 }}>
                          <div className="zn-donut-total">${fmt(categoryData[hoveredCat].value)}</div>
                          <div className="zn-donut-cap">
                            {categoryData[hoveredCat].name} · {(totalCat > 0 ? categoryData[hoveredCat].value / totalCat * 100 : 0).toFixed(0)}%
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div key="total"
                          initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.5 }} transition={{ duration: 0.18 }}>
                          <div className="zn-donut-total">${fmt(totalCat)}</div>
                          <div className="zn-donut-cap">invested</div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                <div className="zn-legend">
                  {categoryData.map((d, i) => (
                    <div key={i} className="zn-legend-row">
                      <span className="zn-legend-chip" style={{ background: catColors[i] }} />
                      <span className="zn-legend-name">{d.name}</span>
                      <span>{(totalCat > 0 ? d.value / totalCat * 100 : 0).toFixed(0)}%</span>
                      <span className="zn-legend-val">${d.value.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Panel>
      </div>

      {/* ── Flips table ────────────────────────────── */}
      <Panel i={2} title={`Flips — ${MONTHS[flipsMonth - 1]} ${chartYear}`}
        desc={monthFlips.length
          ? `${monthFlips.length} flip${monthFlips.length > 1 ? 's' : ''} closed · net ${signed(monthNet)}`
          : 'No flips closed this month'}>
        {monthFlips.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div className="zn-table-head">
              <span>Item</span><span>Condition</span><span>Date</span><span style={{ textAlign: 'right' }}>Profit</span>
            </div>
            {monthFlips.slice(0, 8).map((r, i) => {
              const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
              const p = parseFloat(r.profit) || 0;
              return (
                <div key={i} className="zn-table-row">
                  <div className="zn-table-item">
                    {img && <img src={img} alt="" />}
                    <span className="zn-row-name">{r.skin?.value}</span>
                  </div>
                  <span>{r.condition?.value || 'n/a'}</span>
                  <span className="zn-hide-sm">
                    {new Date(r.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className="zn-table-num" style={{ color: pnlColor(p) }}>{signed(p)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* ── Holdings + recent activity ─────────────── */}
      <div className="zn-cols c-wide">

        <Panel i={3} title="Top unsold items"
          desc={`${favOnly ? 'Tracked in your wishlist' : 'Most expensive first'}${holdingsQuery ? ` · "${holdingsQuery}"` : ''}`}
          tools={
            <>
              <div className="zn-search" style={{ minWidth: 170, flex: '0 1 190px' }}>
                <Search size={15} strokeWidth={2.6} />
                <input placeholder="Search holdings…" value={holdingsQuery}
                  onChange={e => setHoldingsQuery(e.target.value)} />
              </div>
              <button type="button" className={`zn-chip icon ${favOnly ? 'on' : ''}`}
                style={favOnly ? { background: ZONE.hot } : undefined}
                onClick={() => setFavOnly(v => !v)}
                title={favOnly ? 'Show all holdings' : 'Show only skins you track'}>
                <Heart size={15} strokeWidth={2.6} fill={favOnly ? 'currentColor' : 'none'} />
              </button>
              <span className="zn-icon-chip"><Trophy size={16} strokeWidth={2.6} /></span>
            </>
          }>
          <div className="zn-panel-body" style={{ paddingTop: 22 }}>
            {topItems.length === 0 ? (
              <div className="zn-empty" style={{ padding: '30px 14px' }}>
                {favOnly && !holdingsQuery
                  ? 'No tracked skins yet. Tap a heart to start recording a skin’s price.'
                  : holdingsQuery ? 'No holdings match' : 'No unsold items'}
              </div>
            ) : (
              <div className="zn-hold-grid">
                {topItems.map((row, i) => {
                  const img  = findSkinImage(skinsData, row.skin?.value, row.condition?.value);
                  const lp   = getLivePrice(livePrices, row);
                  const mkt  = lp?.starting_at?.price;
                  const buy  = parseFloat(row.buyPrice) || 0;
                  const diff = mkt ? mkt - buy : null;
                  const type = getItemTypes(row).map(t => settings?.itemTypes?.find(it => it.value === t.value)).find(Boolean);
                  const inv     = row.assetId ? inventoryMap?.get(row.assetId) : null;
                  const watched = watchedKeys?.has(holdingKey(row)) ?? false;
                  // Trade-lock countdown: the bar fills as the 7-day lock elapses
                  // and sits full/green once the item is tradable. daysLeft is null
                  // only when the flag is set with no date to reason from, where we
                  // show it locked with an empty bar rather than claim it is free.
                  const { locked, until, daysLeft } = getTradeLock(row, inv);
                  const lockPct = !locked ? 100
                    : daysLeft === null ? 0
                    : Math.round((TRADE_LOCK_DAYS - Math.min(daysLeft, TRADE_LOCK_DAYS)) / TRADE_LOCK_DAYS * 100);
                  const lockLabel = !locked ? 'Tradable now'
                    : daysLeft === null ? 'Trade locked'
                    : `Trade locked · unlocks in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
                  return (
                    <motion.div key={i} className="zn-hcard idm-clickable"
                      style={{ '--zn-hstage': type?.color || CANDY[i % CANDY.length] }}
                      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.05 }}
                      role="button" tabIndex={0} title="Open details"
                      onClick={() => setDetail({ row, img })}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ row, img }); } }}>

                      <div className="zn-hcard-rank">{String(i + 1).padStart(2, '0')}</div>
                      <button type="button" className={`zn-hcard-fav ${watched ? 'on' : ''}`}
                        onClick={e => { e.stopPropagation(); onToggleWatch?.(row.skin?.value, row.condition?.value); }}
                        title={watched
                          ? 'Tracked in your wishlist. Click to stop tracking.'
                          : 'Track this skin’s price in your wishlist'}>
                        <Heart size={14} strokeWidth={2.6} fill={watched ? 'currentColor' : 'none'} />
                      </button>
                      {locked && (
                        <div className="zn-hcard-lock" title={until
                          ? `Locked until ${new Date(until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                          : 'Trade locked'}>
                          <Lock size={13} strokeWidth={2.8} />
                        </div>
                      )}

                      <div className="zn-hcard-stage">{img && <img src={img} alt="" />}</div>

                      <div className="zn-hcard-body">
                        <div className="zn-hcard-name" title={row.skin?.value}>{row.skin?.value}</div>
                        <div className="zn-hcard-meta">
                          {row.condition?.value && <span className="zn-tag">{row.condition.value}</span>}
                          {type && (
                            <span className="zn-tag" style={{ background: type.color, color: readableInk(type.color) }}>
                              {type.label}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="zn-hcard-foot">
                        <span className="zn-hcard-price">${fmt(buy)}</span>
                        {diff !== null && (
                          <span className="zn-hcard-delta" style={{ color: pnlColor(diff) }}>{signed(diff)}</span>
                        )}
                      </div>

                      <div className="zn-hcard-bar" title={lockLabel}>
                        <motion.div className={`fill ${locked ? 'locked' : ''}`}
                          initial={{ width: 0 }} animate={{ width: `${Math.max(3, lockPct)}%` }}
                          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.06 }} />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </Panel>

        <Panel i={4} title="Recent activity" desc="Latest items added" icon={History}>
          <div className="zn-panel-body">
            <div className="zn-timeline">
              {recent.map((row, i) => {
                const img    = findSkinImage(skinsData, row.skin?.value, row.condition?.value);
                const sold   = parseFloat(row.sellPrice) > 0;
                const profit = parseFloat(row.profit) || 0;
                return (
                  <div key={i} className="zn-tl-row">
                    <div className="zn-tl-rail">
                      <span className={`zn-tl-dot ${sold ? (profit >= 0 ? 'green' : 'red') : 'amber'}`} />
                      {i < recent.length - 1 && <span className="zn-tl-line" />}
                    </div>
                    <div className="zn-tl-card">
                      {img && <img src={img} alt="" />}
                      <div className="zn-row-main">
                        <div className="zn-row-name">{row.skin?.value}</div>
                        <div className="zn-row-meta">
                          {new Date(row.timestamp).toLocaleDateString()} · {row.condition?.value || 'n/a'}
                        </div>
                      </div>
                      <div className="zn-tl-right">
                        <div className="zn-row-num" style={{ color: sold ? pnlColor(profit) : ZONE.black }}>
                          {sold ? signed(profit) : `$${fmt(parseFloat(row.buyPrice) || 0)}`}
                        </div>
                        <span className="zn-tl-tag" style={{ background: sold ? ZONE.lime : ZONE.orange }}>
                          {sold ? 'sold' : 'holding'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>
      </div>

      {/* ── Towers + lock queue + gauges ───────────── */}
      <div className="zn-cols c-three">

        <Panel i={5} title="Category towers" desc="Invested value per type">
          <div className="zn-panel-body">
            {categoryData.length === 0 ? (
              <div className="zn-empty" style={{ padding: '30px 14px' }}>No categorized items</div>
            ) : (
              <div className="zn-cyl-track">
                {categoryData.map((d, i) => (
                  <CylinderColumn key={d.name} fill={catColors[i]}
                    label={d.name} value={d.value}
                    share={totalCat > 0 ? d.value / totalCat * 100 : 0}
                    delay={0.15 + i * 0.09} />
                ))}
              </div>
            )}
          </div>
        </Panel>

        <Panel i={6} title="Trade lock queue" desc="Value tied up in trade-protected items" icon={Lock}>
          <div className="zn-panel-body zn-lockq">
            {(() => {
              const total = stats.marketValue || stats.unsoldCost || 1;
              const share = Math.min(100, (stats.lockedCost ?? 0) / total * 100);
              return (
                <>
                  <div className="zn-lockq-row">
                    <div>
                      <span className="zn-lockq-value">${fmt(stats.lockedCost ?? 0)}</span>
                      <span className="zn-lockq-of"> locked of ${fmt(total)}</span>
                    </div>
                    <div className="zn-lockq-pct">{share.toFixed(1)}%</div>
                  </div>
                  <div className="zn-meter">
                    <motion.div className="fill" style={{ background: ZONE.orange }}
                      initial={{ width: 0 }} animate={{ width: `${share}%` }}
                      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} />
                  </div>
                  <div className="zn-lockq-sub">
                    {stats.lockedCount
                      ? `${stats.lockedCount} locked item${stats.lockedCount > 1 ? 's' : ''} · ${stats.unlock7} unlocking within 7 days`
                      : 'No trade-locked items'}
                  </div>
                </>
              );
            })()}
          </div>
        </Panel>

        <Panel i={7} title="How you're doing" desc="Three quick checks on your own trading" icon={Activity}>
          <div className="zn-panel-body">
            {gauges.map(g => <Gauge key={g.name} {...g} />)}
          </div>
        </Panel>
      </div>

      {/* ── Track record ───────────────────────────── */}
      {perf && (
        <Panel i={8} title="Your track record"
          desc={`From ${perf.count} completed sale${perf.count !== 1 ? 's' : ''} · ${perf.wins} made money`}
          icon={Target}>
          <div className="zn-panel-body">
            <div className="zn-perf">
              <div className="zn-burst-wrap" style={{ width: 104, height: 104 }}>
                <div className="zn-burst" />
                <div className="zn-burst-label">
                  <b>{Math.round(perf.winRate)}%</b>
                  <span>win rate</span>
                </div>
              </div>

              <div className="zn-perf-cell">
                <span className="k">Avg per sale</span>
                <div className="big" style={{ color: pnlColor(perf.avg) }}>{signed(perf.avg)}</div>
              </div>

              <div className="zn-perf-cell">
                <span className="k">Best sale</span>
                <div className="zn-perf-item" style={{ '--zn-perf': ZONE.lime }}>
                  {(() => {
                    const img = findSkinImage(skinsData, perf.best.skin?.value, perf.best.condition?.value);
                    return img ? <img src={img} alt="" /> : null;
                  })()}
                  <div className="zn-row-main">
                    <div className="zn-row-name">{perf.best.skin?.value}</div>
                  </div>
                  <span className="zn-row-num" style={{ color: pnlColor(bestP) }}>{signed(bestP)}</span>
                </div>
              </div>

              <div className="zn-perf-cell">
                <span className="k">Worst sale</span>
                <div className="zn-perf-item" style={{ '--zn-perf': ZONE.pink }}>
                  {(() => {
                    const img = findSkinImage(skinsData, perf.worst.skin?.value, perf.worst.condition?.value);
                    return img ? <img src={img} alt="" /> : null;
                  })()}
                  <div className="zn-row-main">
                    <div className="zn-row-name">{perf.worst.skin?.value}</div>
                  </div>
                  <span className="zn-row-num" style={{ color: pnlColor(worstP) }}>{signed(worstP)}</span>
                </div>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {/* ── Value over time ────────────────────────── */}
      {portfolioHistory.length >= 2 && (
        <Panel i={9} title="Value over time"
          desc={`What you paid vs what it's worth now · ${portfolioHistory.length} days tracked`}>
          <div className="zn-panel-body" style={{ padding: '14px 6px 6px' }}>
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={portfolioHistory} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="znOvMv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={ZONE.purple} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={ZONE.purple} stopOpacity={0.06} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false}
                  interval="preserveStartEnd" tickFormatter={d => d.slice(5)} />
                <YAxis tick={TICK} axisLine={{ stroke: AXIS, strokeWidth: 3 }} tickLine={false}
                  tickFormatter={v => `$${v}`} width={54} />
                <Tooltip
                  contentStyle={{ background: ZONE.white, border: `3px solid ${AXIS}`, borderRadius: 0,
                    boxShadow: `4px 4px 0 ${AXIS}`, fontSize: 15, fontWeight: 800, fontFamily: 'Archivo, sans-serif' }}
                  formatter={(v, name) => [`$${fmt(v)}`, name]} />
                <Legend iconType="plainline" wrapperStyle={{ fontSize: 14.5, fontWeight: 800, fontFamily: 'Archivo, sans-serif', paddingTop: 8 }} />
                <Area type="monotone" name="What you paid" dataKey="costBasis" stroke={ZONE.orange} strokeWidth={3}
                  fill="none" dot={false} strokeDasharray="6 4" />
                <Area type="monotone" name="What it's worth" dataKey="marketValue" stroke={AXIS} strokeWidth={3}
                  fill="url(#znOvMv)" dot={false}
                  activeDot={{ fill: ZONE.yellow, stroke: AXIS, strokeWidth: 3, r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      <ZoneFooter stickers={[
        { text: 'overview · the big board', bg: ZONE.purple },
        { text: 'prices via buff163', bg: ZONE.white },
        { text: `${stats.soldCount ?? 0} sold ✦ ${stats.unsoldCount ?? 0} holding`, bg: ZONE.orange, right: true },
      ]} />

      {detail && (
        <ItemDetailModal
          skinsData={skinsData}
          variant="zone"
          kind={parseFloat(detail.row.sellPrice) > 0 ? 'sold' : 'item'}
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
